// Angular imports
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// RxJs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// Constant imports
import { URLConstants } from '../../../../_constants/url-constants';

// model imports
import { APBillParameterDto } from '../../_models/accounts-payable/ap-bill/ap-bill-parameter-dto';
import { APBillResultDto } from '../../_models/accounts-payable/ap-bill/ap-bill-result-dto';
import { TransactionLogParameterDto } from '../../_models/common/transaction-log-parameter-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { APBillReverseParametersDto } from '../../_models/accounts-payable/ap-bill/ap-bill-reverse-parameter-dto';
import { APServiceChargeEditorParameterDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-parameter-dto';
import { APServiceChargeEditorResultDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-result-dto';
import { APServiceChargeRawDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-raw-dto';
import { APServiceChargeEditorSubcategoriesParameterDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-subcategories-parameter-dto';
import { GLSubcategorySelectorResultDto } from '../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { ReverseEditorResultDto } from '../../../../../app/shared-module/_models/reverse-editor-result-dto';

@Injectable({
    providedIn: 'root'
})
export class APBillService {
    constructor(private readonly httpClient: HttpClient) {}

    getAPBillDetails(param: APBillParameterDto): Observable<APBillResultDto> {
        const url = URLConstants.ccCBAPBillApiUrl + `/detail${param.getQueryString()}`;

        return this.httpClient
            .get<APBillResultDto>(url)
            .pipe(map((response: APBillResultDto) => plainToClass(APBillResultDto, response)));
    }

    getAPBillTransactionLog(baseParam: TransactionLogParameterDto): Observable<TransactionLogResultDto[]> {
        const url = `${URLConstants.ccCBAPBillApiUrl}/history${baseParam.getQueryString()}`;

        return this.httpClient
            .get<TransactionLogResultDto[]>(url)
            .pipe(map((response: TransactionLogResultDto[]) => plainToClass(TransactionLogResultDto, response)));
    }

    reverseAPBill(baseParam: APBillReverseParametersDto): Observable<any> {
        return this.httpClient
            .put<string>(`${URLConstants.ccCBAPBillApiUrl}/reverse`, classToPlain(baseParam))
            .pipe(map((responseData: any) => responseData));
    }

    getAPBillReverseEditor(params: APBillParameterDto): Observable<ReverseEditorResultDto> {
        const url = URLConstants.ccCBAPBillApiUrl + `/reverse/editor${params.getQueryString()}`;
        return this.httpClient.get(url).pipe(map((plain: any) => plainToClass(ReverseEditorResultDto, plain)));
    }

    getServiceChargeEditor(param: APServiceChargeEditorParameterDto): Observable<APServiceChargeEditorResultDto> {
        const url = `${URLConstants.ccCBAPBillApiUrl}/servicecharge/editor${param.getQueryString()}`;
        return this.httpClient
            .get<APServiceChargeEditorResultDto>(url)
            .pipe(
                map((response: APServiceChargeEditorResultDto) =>
                    plainToClass(APServiceChargeEditorResultDto, response)
                )
            );
    }

    addServiceCharge(param: APServiceChargeRawDto): Observable<any> {
        const url = `${URLConstants.ccCBAPBillApiUrl}/servicecharge`;
        return this.httpClient.post(url, classToPlain(param));
    }

    getServiceChargeEditorSubcategories(
        params: APServiceChargeEditorSubcategoriesParameterDto
    ): Observable<GLSubcategorySelectorResultDto[]> {
        const url = `${URLConstants.ccCBAPBillApiUrl}/subcategories${params.getQueryString()}`;
        return this.httpClient
            .get<GLSubcategorySelectorResultDto[]>(url)
            .pipe(
                map((response: GLSubcategorySelectorResultDto[]) =>
                    plainToClass(GLSubcategorySelectorResultDto, response)
                )
            );
    }

    deleteAPBill(baseParam: APBillParameterDto): Observable<any> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: classToPlain(baseParam)
        };
        return this.httpClient.delete(URLConstants.ccCBAPBillApiUrl, options);
    }
}
